import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { getOrganizationBranches } from "services/organizationBranches/rest";
import { OrganizationBranchesPageDto, OrganizationBranchListDto } from "services/organizationBranches/restModels";
import { formatDate } from "shared/lib/dateTime";
import { Banner } from "shared/ui/Banner";
import { Button } from "shared/ui/Button";
import { DataTable, DataTableColumn } from "shared/ui/DataTable";
import { EmptyState } from "shared/ui/EmptyState";
import { Pagination } from "shared/ui/Pagination";
import { Skeleton } from "shared/ui/Skeleton";

import { BRANCHES_PAGE_SIZE } from "../../constants";
import { MutedLine } from "../../styles";

interface PartnerBranchesTabProps {
    partnerId: number;
}

const columns: DataTableColumn<OrganizationBranchListDto>[] = [
    {
        key: "name",
        title: "Название",
        role: "primary",
        width: 280,
        render: (branch) => (
            <>
                <div>{branch.name || "—"}</div>
                <MutedLine>ID {branch.id}</MutedLine>
            </>
        ),
    },
    {
        key: "tariff",
        title: "Тариф",
        role: "secondary",
        grow: true,
        render: (branch) => branch.tariff.title,
    },
    {
        key: "nextPayment",
        title: "Оплата",
        role: "secondary",
        width: 110,
        render: (branch) => formatDate(branch.tariff.nextPaymentDate),
    },
];

export const PartnerBranchesTab = ({ partnerId }: PartnerBranchesTabProps) => {
    const history = useHistory();

    const [page, setPage] = useState(1);
    const [data, setData] = useState<OrganizationBranchesPageDto | null>(null);
    const [error, setError] = useState(false);

    const loadBranches = useCallback(() => {
        let ignore = false;

        getOrganizationBranches({
            partnerId,
            skip: (page - 1) * BRANCHES_PAGE_SIZE,
            limit: BRANCHES_PAGE_SIZE,
        })
            .catch(() => null)
            .then((response) => {
                if (ignore) return;

                if (response) setData(response);
                setError(response === null);
            });

        return () => {
            ignore = true;
        };
    }, [partnerId, page]);

    useEffect(() => loadBranches(), [loadBranches]);

    if (error) {
        return (
            <Banner tone="danger" title="Не удалось загрузить заведения">
                <Button size="small" variant="outlined" color="neutral" onClick={loadBranches}>
                    Повторить
                </Button>
            </Banner>
        );
    }

    if (!data) return <Skeleton height="240px" />;

    if (data.total === 0) {
        return (
            <EmptyState
                title="Заведений пока нет"
                description="Партнёр назначается на карточке заведения"
            />
        );
    }

    return (
        <DataTable
            columns={columns}
            rows={data.items}
            rowKey={(branch) => branch.id}
            onRowClick={(branch) => history.push(`/branchesRegister/general/${branch.id}`)}
            footer={
                <Pagination
                    page={page}
                    pageSize={BRANCHES_PAGE_SIZE}
                    total={data.total}
                    onChange={setPage}
                />
            }
        />
    );
};
